import { useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { Modal, ModalField } from '@/components/Modal';
import { PlatformChips } from '@/components/PlatformChips';
import { AppStorePicker } from './AppStorePicker';
import { PRICE_POINTS, errorField, saveMessage, slugify, useCreateApp } from '@/lib/apps';

const FREE = 'Free';

/**
 * Starting an app.
 *
 * On the shared dialog frame, like every other Add on the admin. It had its own
 * — a wider panel on `--panel2`, an `<h2>` where the others have a header row,
 * and its buttons inline at the bottom — which made the one dialog that creates
 * something look unlike the five that edit things.
 */
export function NewAppModal({ onClose }: { onClose: () => void }) {
  const navigate = useNavigate();
  const create = useCreateApp();

  const [name, setName] = useState('');
  const [slug, setSlug] = useState('');
  const [tagline, setTagline] = useState('');
  const [platforms, setPlatforms] = useState<string[]>([]);
  const [price, setPrice] = useState(FREE);
  const [appleId, setAppleId] = useState('');
  const [bundleId, setBundleId] = useState<string | null>(null);

  /*
   * The slug follows the name until somebody edits it, and then stops for good.
   * Without the latch, correcting the slug and then adding a character to the
   * name silently throws the correction away — and the address the app ends up
   * with is not the one that was asked for.
   */
  const [slugEdited, setSlugEdited] = useState(false);

  const editName = (value: string) => {
    setName(value);
    if (!slugEdited) setSlug(slugify(value));
  };

  const failed = create.isError ? saveMessage(create.error) : null;
  // Shown under the box the server named, when it named one.
  const failedField = create.isError ? errorField(create.error) : null;

  return (
    <Modal
      title="New app"
      context="created as a draft"
      submitLabel="Create app"
      submitting={create.isPending}
      // The Apple ID may be left for later, but half a number is not one.
      canSubmit={Boolean(
        name.trim() &&
        slug.trim() &&
        tagline.trim() &&
        (appleId === '' || /^\d{6,12}$/.test(appleId)),
      )}
      // A failure the server did not attach to a field still has to be said,
      // and the frame puts it directly above the button that caused it.
      error={failedField === 'slug' || failedField === 'appleId' ? null : failed}
      onClose={onClose}
      width={520}
      onSubmit={() =>
        create.mutate(
          {
            name: name.trim(),
            slug: slug.trim(),
            tagline: { en: tagline.trim() },
            platforms,
            price: price === FREE ? null : price,
            // Sent only once there is a number: an app made before its App
            // Store record exists is "not linked" until somebody fills it in.
            appleId: appleId || null,
            bundleId,
          },
          {
            // Straight into the editor, because the next thing to do is fill in
            // everything this dialog deliberately did not ask for.
            onSuccess: (app) => navigate(`/apps/${app.id}`),
          },
        )
      }
    >
      <ModalField label="Name">
        <input
          value={name}
          placeholder="Sonasi Meetings"
          onChange={(event) => editName(event.target.value)}
          className="field field-modal"
        />
      </ModalField>

      <ModalField label="Slug">
        <input
          value={slug}
          placeholder="sonasi-meetings"
          onChange={(event) => {
            setSlugEdited(true);
            setSlug(event.target.value);
          }}
          className="field field-mono field-modal"
        />
        {/*
          Inside the field's own label, which is what puts it under the box
          rather than at the foot of the dialog with the network failures.
        */}
        {failedField === 'slug' && failed ? (
          <span className="text-[11.5px]" role="alert" style={{ color: 'var(--danger)' }}>
            {failed}
          </span>
        ) : null}
      </ModalField>

      {/*
        Asked for here, at the first save, because this is usually the moment
        the person has App Store Connect open — an app with no Apple ID has
        no figures, no link and no listing to import. Not insisted on: an app
        being made before its App Store record exists has no number to give,
        and the Apps table says "Not linked" until it does. Picking from the
        list fills the name too, when the name is still untouched.
      */}
      <span className="flex flex-col gap-[5px]">
        <span className="field-label">Apple ID · optional</span>
        <AppStorePicker
          value={appleId}
          onChange={(next, picked) => {
            setAppleId(next);
            // A bundle ID belongs to the app it was picked with; typed over, it goes.
            setBundleId(picked?.bundleId || null);
            if (picked && !name.trim()) editName(picked.name);
          }}
          error={failedField === 'appleId' ? failed : null}
        />
      </span>
      <span className="text-[11px] leading-[1.5]" style={{ color: 'var(--dim)' }}>
        Can be added later, in the app's Publishing card.
      </span>

      <ModalField label="Tagline · English">
        <input
          value={tagline}
          placeholder="What it does, in one line."
          onChange={(event) => setTagline(event.target.value)}
          className="field field-modal"
        />
      </ModalField>
      <span className="text-[11px] leading-[1.5]" style={{ color: 'var(--dim)' }}>
        The other languages are added later, in the editor.
      </span>

      <span className="flex flex-col gap-[5px]">
        <span className="field-label">Platforms</span>
        <PlatformChips selected={platforms} onToggle={setPlatforms} />
      </span>

      <ModalField label="Base price · App Store price point (USD)">
        <select
          value={price}
          onChange={(event) => setPrice(event.target.value)}
          className="field field-modal"
        >
          {PRICE_POINTS.map((point) => (
            <option key={point} value={point}>
              {point}
            </option>
          ))}
        </select>
      </ModalField>
    </Modal>
  );
}
